import { useMemo, useRef } from 'react';
import {
  TEMPLATE_LANGUAGES, TEMPLATE_LIMITS, isAuthentication,
  type TemplateButtonType, type TemplateDraft, type TemplateDraftButton,
} from '@wa/shared';
import { TemplatePreview } from '@/components/TemplatePreview';

interface Props {
  draft: TemplateDraft;
  onChange: (draft: TemplateDraft) => void;
  /** Editing: Meta will not let the name or language change. */
  isEditing: boolean;
}

const CATEGORIES = [
  { value: 'UTILITY', label: 'Utility', hint: 'Order updates, receipts, reminders' },
  { value: 'MARKETING', label: 'Marketing', hint: 'Offers, launches, anything promotional' },
  { value: 'AUTHENTICATION', label: 'Authentication', hint: 'One-time passcodes. WhatsApp writes the message.' },
] as const;

const HEADERS = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'] as const;

const BUTTON_TYPES: ReadonlyArray<{ value: TemplateButtonType; label: string }> = [
  { value: 'QUICK_REPLY', label: 'Quick reply' },
  { value: 'URL', label: 'Link' },
  { value: 'PHONE_NUMBER', label: 'Call' },
  { value: 'COPY_CODE', label: 'Copy code' },
];

/** Highest {{n}} in the text — how many samples Meta will demand. */
function placeholderCount(text: string): number {
  let highest = 0;
  for (const match of text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
    highest = Math.max(highest, Number(match[1]));
  }
  return highest;
}

export function TemplateBuilder({ draft, onChange, isEditing }: Props) {
  const bodyBox = useRef<HTMLTextAreaElement>(null);
  const bodyVariables = useMemo(() => placeholderCount(draft.body), [draft.body]);
  const auth = isAuthentication(draft);

  function set<K extends keyof TemplateDraft>(key: K, value: TemplateDraft[K]) {
    onChange({ ...draft, [key]: value });
  }

  /**
   * Switching category rewrites the form, so it also clears what the new shape
   * cannot carry. Without this, a body typed before choosing Authentication
   * would stay in the draft, be rejected on submit, and be unfixable — the body
   * field is not on screen any more.
   */
  function setCategory(category: TemplateDraft['category']) {
    if (category === draft.category) return;

    if (category === 'AUTHENTICATION') {
      onChange({
        ...draft,
        category,
        headerFormat: 'NONE',
        headerText: undefined,
        headerExample: undefined,
        headerMediaAssetId: null,
        body: '',
        bodyExamples: [],
        footer: undefined,
        buttons: [],
        otpType: draft.otpType ?? 'COPY_CODE',
        addSecurityRecommendation: draft.addSecurityRecommendation ?? true,
        codeExpirationMinutes: draft.codeExpirationMinutes ?? 10,
      });
      return;
    }

    onChange({
      ...draft,
      category,
      addSecurityRecommendation: undefined,
      codeExpirationMinutes: undefined,
      otpType: undefined,
      otpButtonText: undefined,
      otpPackageName: undefined,
      otpSignatureHash: undefined,
      otpAutofillText: undefined,
    });
  }

  /**
   * Inserts the next placeholder at the cursor and grows the sample list with it.
   * Appending {{n}} blindly is how someone ends up with {{1}} {{3}}, which Meta
   * rejects with an error that does not say which number is missing.
   */
  function insertVariable() {
    const next = bodyVariables + 1;
    const token = `{{${next}}}`;
    const box = bodyBox.current;
    const at = box?.selectionStart ?? draft.body.length;
    const body = draft.body.slice(0, at) + token + draft.body.slice(box?.selectionEnd ?? at);

    onChange({ ...draft, body, bodyExamples: [...draft.bodyExamples, ''] });

    queueMicrotask(() => {
      box?.focus();
      box?.setSelectionRange(at + token.length, at + token.length);
    });
  }

  function setButton(index: number, patch: Partial<TemplateDraftButton>) {
    set('buttons', draft.buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  }

  const samplesNeeded = Array.from({ length: bodyVariables }, (_, i) => draft.bodyExamples[i] ?? '');

  return (
    <div className="grid min-h-0 gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="min-w-0 space-y-5">
        <Section title="Basics">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name" hint="Lowercase letters, numbers and underscores">
              <input
                value={draft.name}
                disabled={isEditing}
                onChange={(e) => set('name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                placeholder="order_dispatch_update"
                className={input}
              />
            </Field>

            <Field label="Language" hint={isEditing ? 'Fixed once submitted' : undefined}>
              <select
                value={draft.language}
                disabled={isEditing}
                onChange={(e) => set('language', e.target.value)}
                className={input}
              >
                {TEMPLATE_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
            </Field>
          </div>

          <fieldset className="mt-3">
            <legend className="mb-1.5 text-micro tracking-wide text-(--color-muted) uppercase">Category</legend>
            <div className="grid gap-2 sm:grid-cols-3">
              {CATEGORIES.map((c) => (
                <label
                  key={c.value}
                  className={`tap cursor-pointer rounded-lg border p-2.5 ${
                    draft.category === c.value
                      ? 'border-(--color-brand) bg-(--color-brand-soft)'
                      : 'border-(--color-line) hover:border-(--color-muted)'
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    className="sr-only"
                    checked={draft.category === c.value}
                    onChange={() => setCategory(c.value)}
                  />
                  <span className="block text-small font-medium">{c.label}</span>
                  <span className="mt-0.5 block text-micro text-(--color-muted)">{c.hint}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </Section>

        {auth ? <AuthenticationFields draft={draft} set={set} /> : <>
        <Section title="Header" optional>
          <div className="flex flex-wrap gap-1.5">
            {HEADERS.map((format) => (
              <button
                key={format}
                type="button"
                onClick={() => set('headerFormat', format)}
                className={`tap rounded-lg border px-3 py-1.5 text-small ${
                  draft.headerFormat === format
                    ? 'border-(--color-brand) bg-(--color-brand-soft) text-(--color-brand)'
                    : 'border-(--color-line) text-(--color-muted) hover:text-(--color-ink)'
                }`}
              >
                {format === 'NONE' ? 'None' : format[0] + format.slice(1).toLowerCase()}
              </button>
            ))}
          </div>

          {draft.headerFormat === 'TEXT' && (
            <div className="mt-3 space-y-3">
              <Field label="Header text" count={[draft.headerText?.length ?? 0, TEMPLATE_LIMITS.header]}>
                <input
                  value={draft.headerText ?? ''}
                  maxLength={TEMPLATE_LIMITS.header}
                  onChange={(e) => set('headerText', e.target.value)}
                  placeholder="Order update"
                  className={input}
                />
              </Field>

              {placeholderCount(draft.headerText ?? '') > 0 && (
                <Field label="Sample for {{1}}" hint="Meta will not review a header without one">
                  <input
                    value={draft.headerExample ?? ''}
                    onChange={(e) => set('headerExample', e.target.value)}
                    className={input}
                  />
                </Field>
              )}
            </div>
          )}

          {draft.headerFormat !== 'NONE' && draft.headerFormat !== 'TEXT' && (
            <p className="mt-3 rounded border border-(--color-line) bg-(--color-sunken) px-3 py-2 text-small text-(--color-muted)">
              Meta needs a sample {draft.headerFormat.toLowerCase()} to review this template. Upload one
              on the client's inbox first, then pick it here — sample uploads land in the next build.
            </p>
          )}
        </Section>

        <Section title="Body">
          <Field
            label="Message"
            count={[draft.body.length, TEMPLATE_LIMITS.body]}
            action={
              <button
                type="button"
                onClick={insertVariable}
                className="tap rounded border border-(--color-line) px-2 py-0.5 text-micro text-(--color-muted) hover:text-(--color-ink)"
              >
                Add a value
              </button>
            }
          >
            <textarea
              ref={bodyBox}
              rows={5}
              value={draft.body}
              maxLength={TEMPLATE_LIMITS.body}
              onChange={(e) => set('body', e.target.value)}
              placeholder="Hi {{1}}, your order {{2}} has been dispatched."
              className={`${input} resize-y leading-relaxed`}
            />
          </Field>

          {bodyVariables > 0 && (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {samplesNeeded.map((value, index) => (
                <Field key={index} label={`Sample for {{${index + 1}}}`}>
                  <input
                    value={value}
                    onChange={(e) =>
                      set('bodyExamples', samplesNeeded.map((v, i) => (i === index ? e.target.value : v)))
                    }
                    className={input}
                  />
                </Field>
              ))}
            </div>
          )}
        </Section>

        <Section title="Footer" optional>
          <Field label="Footer text" count={[draft.footer?.length ?? 0, TEMPLATE_LIMITS.footer]}>
            <input
              value={draft.footer ?? ''}
              maxLength={TEMPLATE_LIMITS.footer}
              onChange={(e) => set('footer', e.target.value)}
              placeholder="PSM Softtech Pvt Ltd"
              className={input}
            />
          </Field>
        </Section>

        <Section title="Buttons" optional>
          <div className="space-y-2">
            {draft.buttons.map((button, index) => (
              <div key={index} className="rounded-lg border border-(--color-line) p-2.5">
                {/* The type and its remove control always share one row. Letting
                    the remove button wrap left it stranded on a line of its own
                    for copy-code buttons, which have no label field beside it. */}
                <div className="flex items-center gap-2">
                  <select
                    value={button.type}
                    onChange={(e) => setButton(index, { type: e.target.value as TemplateButtonType })}
                    className={`${input} min-w-0 flex-1`}
                  >
                    {BUTTON_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>

                  <button
                    type="button"
                    onClick={() => set('buttons', draft.buttons.filter((_, i) => i !== index))}
                    aria-label={`Remove button ${index + 1}`}
                    className="tap shrink-0 rounded px-2 py-1.5 text-(--color-muted) hover:text-(--color-bad)"
                  >
                    <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
                      <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>

                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {button.type !== 'COPY_CODE' && (
                    <input
                      value={button.text ?? ''}
                      maxLength={TEMPLATE_LIMITS.buttonText}
                      onChange={(e) => setButton(index, { text: e.target.value })}
                      placeholder="Button label"
                      className={input}
                    />
                  )}

                  {button.type === 'URL' && (
                    <input
                      value={button.url ?? ''}
                      onChange={(e) => setButton(index, { url: e.target.value })}
                      placeholder="https://psmsofttech.com/track/{{1}}"
                      className={input}
                    />
                  )}

                  {button.type === 'URL' && placeholderCount(button.url ?? '') > 0 && (
                    <input
                      value={button.example ?? ''}
                      onChange={(e) => setButton(index, { example: e.target.value })}
                      placeholder="Sample: https://psmsofttech.com/track/4417"
                      className={`${input} sm:col-span-2`}
                    />
                  )}

                  {button.type === 'PHONE_NUMBER' && (
                    <input
                      value={button.phoneNumber ?? ''}
                      onChange={(e) => setButton(index, { phoneNumber: e.target.value })}
                      placeholder="+91 89281 93170"
                      className={input}
                    />
                  )}

                  {button.type === 'COPY_CODE' && (
                    <input
                      value={button.example ?? ''}
                      maxLength={TEMPLATE_LIMITS.copyCode}
                      onChange={(e) => setButton(index, { example: e.target.value })}
                      placeholder="Sample code, e.g. DIWALI20"
                      className={`${input} sm:col-span-2`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Meta's cap. Offering an eleventh button only to have it rejected
              minutes later is worse than not offering it. */}
          {draft.buttons.length < 10 ? (
            <button
              type="button"
              onClick={() => set('buttons', [...draft.buttons, { type: 'QUICK_REPLY', text: '' }])}
              className="tap mt-2 rounded-lg border border-dashed border-(--color-line) px-3 py-1.5 text-small text-(--color-muted) hover:text-(--color-ink)"
            >
              Add a button
            </button>
          ) : (
            <p className="mt-2 text-micro text-(--color-muted)">That is Meta's limit of ten buttons.</p>
          )}
        </Section>
        </>}
      </div>

      {/* Sticky on a wide screen, inline above the form on a narrow one — the
          preview is the point of this screen, so it never scrolls out of reach. */}
      <div className="min-w-0 lg:sticky lg:top-4 lg:self-start">
        <p className="mb-1.5 text-micro tracking-wide text-(--color-muted) uppercase">Preview</p>
        <div className="flex h-[22rem] min-h-0 flex-col overflow-hidden rounded-lg border border-(--color-line)">
          <TemplatePreview draft={draft} />
        </div>
      </div>
    </div>
  );
}

const input =
  'tap w-full min-w-0 rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2 text-body outline-none focus:border-(--color-brand) disabled:opacity-60';

function Section({ title, optional, children }: {
  title: string; optional?: boolean; children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="mb-2 text-small font-semibold">
        {title}
        {optional && <span className="ml-1.5 font-normal text-(--color-muted)">optional</span>}
      </h3>
      {children}
    </section>
  );
}

function Field({ label, hint, count, action, children }: {
  label: string;
  hint?: string;
  count?: [number, number];
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-micro tracking-wide text-(--color-muted) uppercase">{label}</span>
        {action}
        {count && (
          <span className={`ml-auto text-micro tabular-nums ${
            count[0] > count[1] * 0.9 ? 'text-(--color-bad)' : 'text-(--color-muted)'
          }`}>
            {count[0]}/{count[1]}
          </span>
        )}
      </span>
      {children}
      {hint && <span className="mt-1 block text-micro text-(--color-muted)">{hint}</span>}
    </label>
  );
}

/**
 * The authentication form.
 *
 * Deliberately short, because Meta allows almost no choices here: it writes the
 * body, translates it per language, and rejects a template that supplies one —
 * which surfaces as "components param contains unexpected field(s)", an error
 * naming no field. Rather than let someone write a body that will be thrown
 * away, this replaces the body, header, footer and button sections entirely.
 */
function AuthenticationFields({ draft, set }: {
  draft: TemplateDraft;
  set: <K extends keyof TemplateDraft>(key: K, value: TemplateDraft[K]) => void;
}) {
  const oneTap = draft.otpType === 'ONE_TAP';

  return (
    <>
      <Section title="Message">
        <p className="rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2.5 text-small text-(--color-muted)">
          WhatsApp writes and translates the message for an authentication template,
          so there is nothing to type. The preview shows what it will say.
        </p>

        <label className="mt-3 flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            checked={draft.addSecurityRecommendation ?? false}
            onChange={(e) => set('addSecurityRecommendation', e.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-(--color-brand)"
          />
          <span className="min-w-0">
            <span className="block text-small">Add the security warning</span>
            <span className="block text-micro text-(--color-muted)">
              Appends “For your security, do not share this code.”
            </span>
          </span>
        </label>

        <div className="mt-3 max-w-56">
          <Field label="Code expires after" hint="1 to 90 minutes. Leave blank for no expiry line.">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={90}
                value={draft.codeExpirationMinutes ?? ''}
                onChange={(e) =>
                  set('codeExpirationMinutes', e.target.value === '' ? null : Number(e.target.value))
                }
                placeholder="10"
                className={input}
              />
              <span className="shrink-0 text-small text-(--color-muted)">minutes</span>
            </div>
          </Field>
        </div>
      </Section>

      <Section title="Button">
        <div className="flex flex-wrap gap-1.5">
          {(['COPY_CODE', 'ONE_TAP'] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => set('otpType', type)}
              className={`tap rounded-lg border px-3 py-1.5 text-small ${
                (draft.otpType ?? 'COPY_CODE') === type
                  ? 'border-(--color-brand) bg-(--color-brand-soft) text-(--color-brand)'
                  : 'border-(--color-line) text-(--color-muted) hover:text-(--color-ink)'
              }`}
            >
              {type === 'COPY_CODE' ? 'Copy code' : 'One-tap autofill'}
            </button>
          ))}
        </div>

        <p className="mt-2 text-micro text-(--color-muted)">
          {oneTap
            ? 'WhatsApp hands the code straight to your Android app. It needs the app’s details below.'
            : 'The customer taps to copy the code, then pastes it into your app or site. Works everywhere.'}
        </p>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <Field label="Button label" hint="Leave blank for WhatsApp’s own translated label">
            <input
              value={draft.otpButtonText ?? ''}
              maxLength={TEMPLATE_LIMITS.buttonText}
              onChange={(e) => set('otpButtonText', e.target.value)}
              placeholder={oneTap ? 'Autofill' : 'Copy code'}
              className={input}
            />
          </Field>

          {oneTap && (
            <>
              <Field label="Android package name">
                <input
                  value={draft.otpPackageName ?? ''}
                  onChange={(e) => set('otpPackageName', e.target.value)}
                  placeholder="com.psmsofttech.app"
                  className={input}
                />
              </Field>

              <Field label="Signature hash" hint="The 11-character hash of your app’s signing key">
                <input
                  value={draft.otpSignatureHash ?? ''}
                  onChange={(e) => set('otpSignatureHash', e.target.value)}
                  className={input}
                />
              </Field>

              <Field label="Autofill label" hint="Leave blank for WhatsApp’s default">
                <input
                  value={draft.otpAutofillText ?? ''}
                  maxLength={TEMPLATE_LIMITS.buttonText}
                  onChange={(e) => set('otpAutofillText', e.target.value)}
                  placeholder="Autofill"
                  className={input}
                />
              </Field>
            </>
          )}
        </div>
      </Section>
    </>
  );
}
